import dayjs from 'dayjs'
import ignore from 'ignore'
import pako from 'pako'
import store from '~/store'
import { removeDirHandle, selectHandleFolderHash, selectUserHandleById, syncDirHandlesWithBucket, updateFolderHash, updateSecurityStatus } from '~/store/dirHandleSlice'
import { FileHandleStore } from '~/utils/FileHandleStore'
import { FolderHasher } from '~/utils/folder-hasher'
import GitIgnore from './.gitignore'
import { TarWriter } from './tarjs'

// Update imports to use localSyncService
import { SecurityScanStatus, deleteLocalSyncRepo, uploadLocalSyncRepo } from '~/services/localSyncService'

class CronScheduler {
  private fileHandleStore: FileHandleStore
  private folderHasher: FolderHasher
  private isRunning: boolean = false
  private interval: NodeJS.Timeout | null = null
  private ig: ReturnType<typeof ignore>

  constructor(private checkIntervalSeconds: number = 1) {
    this.fileHandleStore = new FileHandleStore()
    this.folderHasher = new FolderHasher()
    this.ig = ignore().add(GitIgnore)
  }

  async start() {
    if (this.interval) {
      return
    }

    await this.fileHandleStore.init()

    // First ensure we've synchronized with the server
    await store.dispatch(syncDirHandlesWithBucket()).unwrap()
      .catch(error => console.error('Failed to sync repositories with server:', error))

    // Force process all directories at startup to ensure they're synced
    await this.forceProcessDirectories()

    // Schedule subsequent runs with normal processing
    this.interval = setInterval(() => this.processDirectories(), this.checkIntervalSeconds * 1000)
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  /**
   * Force process all directories regardless of hash changes
   * This ensures all repositories are uploaded at startup
   */
  private async forceProcessDirectories() {
    if (this.isRunning) {
      return
    }

    this.isRunning = true
    try {
      const state = store.getState()
      const currentUserEmail = state.dirHandle._currentUserEmail
      if (!currentUserEmail) {
        return // No user logged in
      }

      const userState = state.dirHandle.userStates[currentUserEmail]
      if (!userState || !userState.isInitialized) {
        return // User state not initialized
      }

      console.info('🔄 Force processing all repositories at startup...')

      // Process all handles belonging to current user
      for (const { id } of userState.handles) {
        try {
          const storedHandle = await this.fileHandleStore.getHandleById(id)
          if (storedHandle) {
            // Check if folder still exists on disk
            const folderExists = await this.fileHandleStore.isFolderExists(id)
            if (!folderExists) {
              console.warn(`Directory for handle ${id} no longer exists on disk. Removing handle.`)
              await this.removeNonExistentFolder(id, storedHandle.handle.name)
              continue // Skip processing this directory
            }

            // Process directory with force flag
            await this.processDirectory(storedHandle.handle, id, true)
          }
        } catch (error) {
          console.error(`🔄 Error processing directory handle ${id}:`, error)
        }
      }

      console.info('🔄 Initial repository processing complete')

      // After all directories are processed, sync with server one more time
      // This ensures the server has the latest list of repositories
      await store.dispatch(syncDirHandlesWithBucket()).unwrap()
        .catch(error => console.error('Failed to sync repositories with server after initial processing:', error))
    } finally {
      this.isRunning = false
    }
  }

  private async processDirectories() {
    if (this.isRunning) {
      return
    }

    this.isRunning = true
    try {
      // Get current user's handles from Redux store
      const state = store.getState()
      const currentUserEmail = state.dirHandle._currentUserEmail
      if (!currentUserEmail) {
        return // No user logged in
      }

      const userState = state.dirHandle.userStates[currentUserEmail]
      if (!userState || !userState.isInitialized) {
        return // User state not initialized
      }

      // Process only handles belonging to current user
      for (const { id } of userState.handles) {
        try {
          const storedHandle = await this.fileHandleStore.getHandleById(id)
          if (storedHandle) {
            // Check if folder still exists on disk
            const folderExists = await this.fileHandleStore.isFolderExists(id)
            if (!folderExists) {
              console.warn(`Directory for handle ${id} no longer exists on disk. Removing handle.`)
              // Call handleRemoveLocation from inside CronScheduler
              await this.removeNonExistentFolder(id, storedHandle.handle.name)
              continue // Skip processing this directory
            }

            // Check if this repository has security threats - skip processing if it does
            const handleInfo = userState.handles.find(h => h.id === id)
            if (handleInfo?.securityStatus === 'THREATS_FOUND') {
              console.warn(`Skipping directory ${storedHandle.handle.name} with security threats`)
              continue // Skip processing this directory
            }

            // Process directory normally (not forced)
            await this.processDirectory(storedHandle.handle, id, false)
          }
        } catch (error) {
          console.error(`🔄 Error processing directory handle ${id}:`, error)
        }
      }
    } finally {
      this.isRunning = false
    }
  }

  public async processDirectory(
    directoryHandle: FileSystemDirectoryHandle,
    handleId: string,
    forceUpload: boolean = false
  ) {
    // Check if this repository has security threats - skip processing if it does
    const state = store.getState()
    const handleInfo = selectUserHandleById(state, handleId)
    if (handleInfo?.securityStatus === 'THREATS_FOUND') {
      console.warn(`Skipping directory ${directoryHandle.name} with security threats`)
      return // Skip processing this directory
    }

    const currentHash = await this.folderHasher.getFolderHash(directoryHandle)

    // Get current handle's hash using selector with default current user
    const lastFolderHash = selectHandleFolderHash(state, handleId)

    // If the hash hasn't changed and we're not forcing upload, skip the process
    if (lastFolderHash === currentHash && !forceUpload) {
      return
    }

    const files: File[] = []

    // Recursively collect all files
    const collectFiles = async (handle: FileSystemDirectoryHandle, path: string = '') => {
      try {
        for await (const entry of (handle as any).values()) {
          const entryPath = path ? `${path}/${entry.name}` : entry.name

          // Skip if path should be excluded based on folder hasher rules
          if (!this.folderHasher.shouldIncludePath(entryPath)) {
            continue
          }

          if (entry.kind === 'file') {
            try {
              const file = await entry.getFile()
              const newFile = new File([file], file.name, { type: file.type })
              Object.defineProperty(newFile, 'webkitRelativePath', {
                writable: true,
                value: entryPath
              })
              files.push(newFile)
            } catch (fileError) {
              console.error(`🔄 Error reading file ${entryPath}:`, fileError)
            }
          } else if (entry.kind === 'directory') {
            await collectFiles(entry, entryPath)
          }
        }
      } catch (dirError) {
        console.error(`🔄 Error reading directory ${path || handle.name}:`, dirError)
      }
    }

    try {
      await collectFiles(directoryHandle)

      if (files.length === 0) {
        console.info(`🔄 No files found in directory ${directoryHandle.name}`)
        return
      }

      const tarFile = await this.createCompressedTarArchive(files, directoryHandle.name, handleId)
      console.info('🔄 Tar file created', tarFile)

      // Update security status to SCANNING
      store.dispatch(updateSecurityStatus({
        id: handleId,
        securityStatus: 'SCANNING'
      }))

      // Upload the archive using the new service
      await uploadLocalSyncRepo(tarFile, {
        onProgress: progress => {
          console.info(`🔄 Upload progress for ${directoryHandle.name}: ${progress}%`)
        },
        onVirusScanStatus: (status, message) => {
          console.info(`🔄 Virus scan status for ${directoryHandle.name}: ${status}${message ? ` - ${message}` : ''}`)

          // Update security status in Redux store based on scan status
          if (status === 'THREATS_FOUND' || status === 'CLEAN' || status === 'SCANNING') {
            store.dispatch(updateSecurityStatus({
              id: handleId,
              securityStatus: status as SecurityScanStatus
            }))
            setTimeout(() => {
              console.log('store state', store.getState().dirHandle)
            }, 1000)
          }

          // Map other statuses to our security status format
          if (status === 'NO_THREATS_FOUND' || status === 'SCAN_SKIPPED') {
            store.dispatch(updateSecurityStatus({
              id: handleId,
              securityStatus: 'CLEAN'
            }))
          }
        },
        onComplete: async (path, virusScanStatus) => {
          console.info(`🔄 Upload complete for ${directoryHandle.name}, path: ${path}`)

          // Handle virus scan status if provided
          if (virusScanStatus) {
            // Map NO_THREATS_FOUND to CLEAN for consistency
            const normalizedStatus = virusScanStatus === 'NO_THREATS_FOUND' || virusScanStatus === 'SCAN_SKIPPED'
              ? 'CLEAN'
              : virusScanStatus

            store.dispatch(updateSecurityStatus({
              id: handleId,
              securityStatus: normalizedStatus as SecurityScanStatus
            }))
          }

          // Only update folder hash if security status is clean
          if (virusScanStatus !== 'THREATS_FOUND') {
            // Dispatch redux action to update folder hash
            await store.dispatch(
              updateFolderHash({
                id: handleId,
                folderHash: currentHash
              })
            )

            if (forceUpload) {
              console.info(
                `🔄 Initial upload of ${directoryHandle.name} complete, ` +
                `updated folder hash to ${currentHash.slice(0, 7)}`
              )
            } else {
              console.info(
                `🔄 Changes detected in ${directoryHandle.name}, ` +
                `updated folder hash for ${directoryHandle.name} to ${currentHash.slice(0, 7)}`
              )
            }
          } else {
            console.warn(`🔄 Security threats detected in ${directoryHandle.name}, repository will not be synchronized`)
          }
        },
        onError: (error, virusScanStatus) => {
          console.error(`🔄 Error uploading ${tarFile.name}:`, error)

          // Handle virus scan status from error
          if (virusScanStatus === 'THREATS_FOUND') {
            store.dispatch(updateSecurityStatus({
              id: handleId,
              securityStatus: 'THREATS_FOUND'
            }))
            console.warn(`🔄 Security threat detected in ${directoryHandle.name}`)

            // Don't throw an exception for security threats - just report them and stop
            return
          }

          // Only throw for non-security related errors
          throw new Error(`Upload failed: ${error}`)
        }
      })
    } catch (error: unknown) {
      // If this is a virus detection error, handle it without re-throwing
      if (error instanceof Error &&
        (error.message.includes('Virus') || error.message.includes('security threat'))) {
        console.warn(`🔄 Virus or security threat detected in ${directoryHandle.name}, repository will not be synchronized`)

        // Update security status
        store.dispatch(updateSecurityStatus({
          id: handleId,
          securityStatus: 'THREATS_FOUND'
        }))

        // Don't rethrow the error
        return
      }

      // Log and rethrow other types of errors
      console.error(`🔄 Failed to process ${directoryHandle.name}:`, error)
      throw error
    }
  }

  private async createCompressedTarArchive(
    files: File[],
    directoryName: string,
    handleId: string
  ): Promise<File> {
    console.log(`🔄 Creating compressed tar archive for ${directoryName} with ${files.length} files`)

    const tarWriter = new TarWriter()
    let fileCount = 0;
    let totalBytes = 0;

    for (const file of files) {
      try {
        const fileContent = await file.arrayBuffer()
        totalBytes += fileContent.byteLength;
        const relativePath = file.webkitRelativePath

        // Skip files that match gitignore patterns
        if (!this.ig.ignores(relativePath)) {
          // Wrap all files within the directory name
          const pathInArchive = `${directoryName}/${relativePath}`
          await tarWriter.addFile(pathInArchive, new Uint8Array(fileContent))
          fileCount++;
        }
      } catch (error) {
        console.error(`🔄 Error processing file ${file.webkitRelativePath}:`, error)
      }
    }

    const tarBlob = await tarWriter.write()
    const tarArray = new Uint8Array(await tarBlob.arrayBuffer())

    // Convert to standard array buffer and then gzip
    const arrayBuffer = tarArray.buffer

    const gzippedArray = pako.gzip(new Uint8Array(arrayBuffer))

    const date = dayjs().format('YYYYMMDDHHmmss')
    const tarFileName = `${directoryName}.tar.gz`
    const newTarFileName = `${date}-${handleId}___${tarFileName}`

    // Create file with the proper BlobPart[] type
    return new File([new Uint8Array(gzippedArray)], newTarFileName, {
      type: 'application/gzip'
    })
  }

  private async removeNonExistentFolder(id: string, dirName: string) {
    try {
      // Remove from Redux store
      await store.dispatch(removeDirHandle(id)).unwrap()

      // Remove uploads from the S3 bucket using new service
      try {
        await deleteLocalSyncRepo(dirName)
        console.info(
          `🔄 Removed repository data for ${dirName} because folder no longer exists on disk`
        )
      } catch (deleteError) {
        console.error(`🔄 Failed to delete remote data for ${dirName}:`, deleteError)
      }
    } catch (error) {
      console.error(`🔄 Failed to clean up handle for non-existent folder ${id}:`, error)
    }
  }
}

// Create and export a singleton instance
export const cronScheduler = new CronScheduler()

// Export the class for testing or custom initialization
export { CronScheduler }
